/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : Jul 27, 2025
Folder          : src/components/button
File            : ButtonCompDark.tsx
Description     : 
    This reusable component is a dark button.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/


// Import React Systems
import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from "react-router-dom";


// Import Firebase Systems
import {  getAuth,
          signOut,
          onAuthStateChanged } from "firebase/auth";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../../../backend/firebaseConfig";

// Import Functions
import { logoutUser } from "../../../functions/logoutUser"

// Import Icon Atom
import Icon from "../../atoms/icon/Icon"
import Text from "../../atoms/text/Text"
import ButtonComp from "../../atoms/button/Button"
import DeletionRequestModal from "../modal/DeletionRequestModal"

// Import Assets
import logout_icon from "../../../../assets/logout.png"
import notification from "../../../../assets/notification.png"

// Import CSS
// import "./styles.css"

// Import Customized Components
import {  markNotificationAsRead,
          deleteNotification,
          addNotification } from "../../../functions/notifyComp";

// Imports MUI Components
import {  Box,
          Button,
          Stack,
          Typography,
          Grid,
          CardContent,
          Divider,
          List,
          ListItem,
          ListItemText,
          ListItemIcon,
          ListItemAvatar,
          Avatar,
          TextField,
          Tooltip,
          Snackbar,
          Alert as MuiAlert } from "@mui/material"
import Badge from '@mui/material/Badge';
import MailIcon from '@mui/icons-material/Mail';

const Notification = ({
  setShowNotifications,
  unreadCount,
  showNotifications,
  notifications,
  setShowNotificationsModal,
  setNotifications,
  userDetails = {},
}) => { 

  // Listen for optimistic notifications dispatched by the contact Section
  useEffect(() => {
    const onNotificationSent = (e) => {
      const detail = e?.detail;
      if (!detail || !detail.uid || detail.uid !== userDetails?.uid) return;
      setNotifications((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        if (list.some((n) => n.id === detail.id)) return list;
        return [{ id: detail.id, ...detail }, ...list];
      });
    };
    window.addEventListener("notification-sent", onNotificationSent);
    return () => window.removeEventListener("notification-sent", onNotificationSent);
  }, [userDetails?.uid, setNotifications]);

  const auth = getAuth();
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [rootNotifications, setRootNotifications] = useState([]);
  const [userNotifications, setUserNotifications] = useState([]);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [selectedDeletionRequest, setSelectedDeletionRequest] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

    // Handler to read notification button result
  const handleNotificationToggle = (result) => {
    setShowNotifications(prev => !prev);
  };

  const handleMarkAsRead = (notificationId) => {
    markNotificationAsRead(auth.currentUser, notificationId);
    setNotifications(prevNotifications =>
      prevNotifications.map((notification) =>
        notification.id === notificationId ? { ...notification, read: true } : notification
      )
    );
  };

  const handleDeletionRequestClick = (notification) => {
    if (notification.type === 'account-deletion-request' && userDetails?.role === 'DentistOwner') {
      setSelectedDeletionRequest(notification);
      setShowDeletionModal(true);
      setShowNotifications(false);
    }
  };

  const handleApproveDeletion = async (requestData) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const idToken = await user.getIdToken();
      const response = await fetch('/api/handle-deletion-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          requestId: requestData.deletionRequestId,
          action: 'approve',
          approverUid: userDetails?.uid,
          approverName: `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to approve deletion');
      }

      setSnackbarMessage('Account deletion approved successfully');
      setShowSuccessSnackbar(true);
      setShowDeletionModal(false);
      
      // Mark notification as read
      handleMarkAsRead(selectedDeletionRequest.id);
    } catch (error) {
      console.error('Error approving deletion:', error);
      setSnackbarMessage('Failed to approve deletion request');
      setShowErrorSnackbar(true);
    }
  };

  const handleRejectDeletion = async (requestData) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const idToken = await user.getIdToken();
      const response = await fetch('/api/handle-deletion-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          requestId: requestData.deletionRequestId,
          action: 'reject',
          approverUid: userDetails?.uid,
          approverName: `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reject deletion');
      }

      setSnackbarMessage('Account deletion request rejected');
      setShowSuccessSnackbar(true);
      setShowDeletionModal(false);
      
      // Mark notification as read
      handleMarkAsRead(selectedDeletionRequest.id);
    } catch (error) {
      console.error('Error rejecting deletion:', error);
      setSnackbarMessage('Failed to reject deletion request');
      setShowErrorSnackbar(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const getNotificationFields = (notification = {}) => {
  // common locations/aliases
  const message =
    notification.message ||
    notification.msg ||
    notification.body ||
    (notification.data && (notification.data.message || notification.data.msg)) ||
    "";
  const name =
    notification.name ||
    notification.fullName ||
    notification.fromName ||
    (notification.data && notification.data.name) ||
    "";
  const email =
    notification.email ||
    notification.fromEmail ||
    (notification.data && notification.data.email) ||
    "";
  return { name, email, message };
};

  const parseNotificationDate = (notif) => {
    if (!notif) return new Date(0);
    const ts = notif.createdAt || notif.date || notif.timestamp || notif.timeStamp;
    if (!ts) return new Date(0);

    // Firestore Timestamp object with toDate()
    if (typeof ts.toDate === "function") {
      try { return ts.toDate(); } catch (e) { /* fallthrough */ }
    }

    // Firestore-like object { seconds: number }
    if (typeof ts === "object" && ts.seconds) {
      return new Date(ts.seconds * 1000);
    }

    // numeric seconds or milliseconds
    if (typeof ts === "number") {
      // heuristics: if seconds (10 digits) convert to ms
      return ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
    }

    // string date/time
    try {
      const d = new Date(String(ts));
      if (!isNaN(d)) return d;
    } catch (e) { /* ignore */ }

    return new Date(0);
  };

  const formatNotificationTime = (notif) => {
    const dt = parseNotificationDate(notif);
    if (isNaN(dt)) return "";
    return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatNotificationDate = (notif) => {
  const dt = parseNotificationDate(notif);
  if (!dt || isNaN(dt.getTime())) return "";
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
};

// helper: convert any YYYY-MM-DD in a message to MM-DD-YYYY
const normalizeMessageDates = (msg) => {
  if (!msg) return msg;
  return String(msg).replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_m, y, mo, d) => `${mo}-${d}-${y}`);
};

 
useEffect(() => {
  if (!db) return;

  const privilegedRoles = [
    "DentistOwner",
    "AssociateDentist",
    "ClinicStaff",
  ];

  if (!privilegedRoles.includes(userDetails?.role)) {
    setRootNotifications([]);
    return;
  }

  let unsubscribed = false;
  const q = query(collection(db, "notifications"));
  const unsub = onSnapshot(
    q,
    (snap) => {
      if (unsubscribed) return;
      const items = [];
      snap.forEach((doc) => items.push({ id: doc.id, ...doc.data() }));
      setRootNotifications(items);
    },
    (err) => {
      if (unsubscribed) return;
      if (err.code !== "permission-denied") {
        console.error("root notifications snapshot error:", err);
      }
      setRootNotifications([]);
    }
  );
  return () => {
    unsubscribed = true;
    unsub();
  };
}, [db, userDetails?.role]);

useEffect(() => {
   if (!db || !userDetails?.uid) {
     setUserNotifications([]);
     return;
   }
   
   let unsubscribed = false;
   const userNotifColl = collection(db, "notifications", userDetails.uid, "userNotifications");
   const q = query(userNotifColl);
   const unsub = onSnapshot(
     q,
     (snap) => {
       if (unsubscribed) return;
       const items = [];
       snap.forEach((doc) => items.push({ id: doc.id, ...doc.data() }));
       // keep newest first
       setUserNotifications(items);
     },
    (err) => {
      if (unsubscribed) return;
      if (err.code !== "permission-denied") {
        console.error("userNotifications snapshot error:", err);
      }
      setUserNotifications([]);
     }
   );
   return () => {
     unsubscribed = true;
     unsub();
   };
 }, [db, userDetails?.uid]);

// merge prop notifications + root notifications, dedupe by id, then filter/sort
const displayNotifications = (() => {
  const map = new Map();
  (notifications || []).forEach((n) => { if (n?.id) map.set(n.id, n); });
  // always include per-user subcollection items
  (userNotifications || []).forEach((n) => { if (n?.id && !map.has(n.id)) map.set(n.id, n); });
  // include root notifications if missing
  (rootNotifications || []).forEach((n) => { if (n?.id && !map.has(n.id)) map.set(n.id, n); });

  const all = Array.from(map.values());
  
  const filtered = all.filter((notification) => {
    if (!notification) return false;

    // prefer explicit message presence (contact form uses `message`)
    const msg = String(notification.message || notification.msg || notification.body || "").trim();
    if (msg.length > 0) return true;

    // also allow notifications explicitly targeted to the user's role
    if (notification.recipientRole && notification.recipientRole === userDetails?.role) return true;

    return false;
  });

  return filtered.slice().sort((a, b) => parseNotificationDate(b) - parseNotificationDate(a));
})();
  
  return (
    <div>
      <Tooltip title={!showNotifications ? "Notifications" : null} arrow>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "20px"
          }}>

          {/* Notifications Button Container*/}
          <Box
            sx={{
              position: "relative" 
            }}
          >
          
            <Box
              display={{
                    xs : "flex",
                    sm : "flex",
                    md : "none",
                    lg : "none",
                    xl : "none"
              }}  
            >
            {/* Notifications Button */}
            <ButtonComp
              buttonId="notification"
              variant="contained"
              noElevate={true}
              noRipple={true}
              color="light"
              paddings="notif"
              background="none"
              font="default"
              weight="default"
              border="default"
              transform="default"
              selected={true}
              keys={false}
              hoverTextColor="light"
              hoverBackground="none"
              hoverBorder="default"
              notif={true}
              noRead={false}
              type="default"
              justify="default"
              iconned={true}
              className="dropdown-container"
              clickPass={handleNotificationToggle}
              source={require("../../../../assets/notification_light_filled.png")}
              size="small"/>
            </Box>
          
            <Box
              display={{
                    xs : "none",
                    sm : "none",
                    md : "flex",
                    lg : "flex",
                    xl : "flex"
              }}  
            >
            {/* Notifications Button */}
            <ButtonComp
              buttonId="notification"
              variant="contained"
              noElevate={true}
              noRipple={true}
              color="light"
              paddings="notif"
              background="none"
              font="default"
              weight="default"
              border="default"
              transform="default"
              selected={true}
              keys={false}
              hoverTextColor="light"
              hoverBackground="none"
              hoverBorder="default"
              notif={true}
              noRead={false}
              type="default"
              justify="default"
              iconned={true}
              className="dropdown-container"
              clickPass={handleNotificationToggle}
              source={require("../../../../assets/notification.png")}
              size="small"/>
            </Box>
            
              {/* Notification Count */}
              {unreadCount > 0 && (
                // {unreadCount == 0 && (
                <div>
                  <Typography
                    sx={{
                      position: "absolute",
                      top: "8px",
                      right: {
                        xs: "1px",
                        sm: "1px",
                        md: "-1px",
                        lg: "-1px",
                        xl: "-1px",
                      },
                      background: "#9F4223",
                      color: "white",
                      borderRadius: "50%",
                      padding: {
                        xs : unreadCount < 10 ? "4px 8px" : unreadCount > 99 ? "4px 4px" : "5px 7px",
                        sm : unreadCount < 10 ? "4px 8px" : unreadCount > 99 ? "4px 4px" : "5px 7px",
                        md : unreadCount < 10 ? "5px 10px" : unreadCount > 99 ? "5px 5px" : "5px 7px",
                        lg : unreadCount < 10 ? "5px 10px" : unreadCount > 99 ? "5px 5px" : "5px 7px",
                        xl : unreadCount < 10 ? "5px 10px" : unreadCount > 99 ? "5px 5px" : "5px 7px",
                      },
                      fontSize: {
                        xs: "9px",
                        sm: "9px",
                        md: "10px",
                        lg: "10px",
                        xl: "10px",
                      },
                    }}>
                      {unreadCount < 99 ? (unreadCount) : "99+"}
                  </Typography>
                </div>
              )}

              {showNotifications && (

              <div>

                <Box
                  sx={{
                    position: "absolute",
                    top: "50px",
                    right: "0",
                    background: "white",
                    border: "1px solid #FEFEFE",
                    borderRadius: "10px",
                    boxShadow: "0 0.5px 10px rgba(46, 45, 45, 0.6)",
                    width: "350px",
                    zIndex: 1000,
                    padding: 0, // Remove extra padding here
                  }}>

                  {/* Header */}
                  <Box
                    sx={{
                      borderBottom: "1px solid #585858",
                      padding: "10px",
                    }}>

                      <Stack
                        direction="row"
                        justifyContent="space-between">

                  <Text
                    text="Notifications"
                    variant="notificationH3"/>

                      <Stack
                        direction="row"
                        gap={1.5}>
                  <Text
                    text={unreadCount}
                    variant="notificationNumber"/>


                    <Typography
                          onClick={() => {
                        setShowNotifications(false);
                        setShowNotificationsModal(true);
                      }}
                          gutterBottom
                          variant="h3"
                          component="div"
                          sx={{
                            cursor: "pointer",
                            fontWeight: "300",
                            fontSize: "14px",
                            margin: "4px 0px 0px 0px",
                            color: "#2C251E",
                              ":hover": {
                                textDecoration: "underline",
                              }
                            }}
                          >View all
                        </Typography>
</Stack>
                    </Stack>



                  </Box>


                



                  <ul
    style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      maxHeight: "300px",
      overflowY: "auto"
    }}>

    {displayNotifications.length === 0 ? (

      <li
        style={{
          padding: "10px",
          textAlign: "center",
          color: "#888"
        }}>No notifications
      </li>

    ) : (
      displayNotifications.map(notification => (

        <li
          onClick={() => {
            if (notification.type === 'account-deletion-request') {
              handleDeletionRequestClick(notification);
            } else {
              handleMarkAsRead(notification.id);
            }
          }}
          key={notification.id}
          style={{
            backgroundColor:
              notification.read ? "transparent" : "rgba(46, 45, 45, 0.1)",
            borderBottom: "1px solid rgba(46, 45, 45, .2)",
            cursor: "pointer",
          }}>

          <Box sx={{ width: "100%" }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "center",
                padding: "10px",
                // keep hover highlight consistent
                ":hover": {
                  backgroundColor: "rgba(46, 45, 45, 0.03)",
                }
              }}>
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                 {(() => {
                   // show notification.message (fallbacks included) directly in dropdown
                   const message =
                     notification?.message ||
                     notification?.msg ||
                     notification?.body ||
                     (notification?.data && (notification.data.message || notification.data.msg)) ||
                     notification?.title ||
                     "";
                  const sender =
                    notification?.name ||
                    notification?.fullName ||
                    notification?.fromName ||
                    (notification?.data && notification.data.name) ||
                    null;
                  
                  const isDeletionRequest = notification?.type === 'account-deletion-request';

                  return (
                    <div>
                      {sender && <div style={{ fontWeight: 600, marginBottom: 6 }}>{sender}{' '}sent a message</div>}
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                        {isDeletionRequest && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: "2px" }}>
                            <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        <div style={{ display: "block", whiteSpace: "pre-line", wordBreak: "break-word", textAlign: "justify", textAlignLast: "left", textJustify: "inter-word", wordSpacing: "normal", hyphens: "auto", WebkitHyphens: "auto", msHyphens: "auto", color: "#222", flex: 1 }}>
                          {normalizeMessageDates(String(message || "")) || "(no message)"}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </Box>

              <Box
                sx={{
                 backgroundColor: notification.read ? "transparent" : "#2C6785",
                  width: "20px",
                  height: "10px",
                  borderRadius: "50px",
                }}
              />
            </Stack>

            {/* time shown bottom-right */}
            <div
               style={{
                 padding: "0 10px 8px 10px",
                 display: "flex",
                 justifyContent: "space-between",
                 fontSize: 12,
                 color: "#666",
               }}
             >
              <div style={{ textAlign: "left" }}>{formatNotificationDate(notification)}</div>
              <div style={{ textAlign: "right" }}>{formatNotificationTime(notification)}</div>
             </div>
          </Box>

        </li>

      ))
    )}

  </ul>

                  <div
                    style={{
                      textAlign: "end",
                      justifyContent: "end",
                      padding: "10px",
                      display: "flex",
                    borderTop: "1px solid #585858",
                    }}>
                      <Box>
                        <Typography
                          onClick={(e) => {
                            e.stopPropagation();
                            // Mark all notifications as read for this user
                            notifications.forEach(notification => {
                              handleMarkAsRead(notification.id);
                            });
                          }}
                          gutterBottom
                          variant="h3"
                          component="div"
                          sx={{
                            cursor: "pointer",
                            fontWeight: "300",
                            // padding: "10px 10px",
                            fontSize: "14px",
                            marginBottom: "0px",
                            // bottom: "-8px",
                            // textAlign: "center",
                            color: "#2C6785",
                              ":hover": {
                                textDecoration: "underline",
                              }
                            }}
                          >Mark all as read
                        </Typography>
                      </Box>

                  </div>

                </Box>
                        
              </div>
                              
              )}

          </Box>

        </Box>
      </Tooltip>

      {/* Deletion Request Modal */}
      <DeletionRequestModal
        isOpen={showDeletionModal}
        onClose={() => setShowDeletionModal(false)}
        requestData={selectedDeletionRequest}
        onApprove={handleApproveDeletion}
        onReject={handleRejectDeletion}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={5000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          variant="filled"
          icon={false}
          sx={{
            backgroundColor: "#558363",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={5000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setShowErrorSnackbar(false)}
          severity="error"
          variant="filled"
          icon={false}
          sx={{
            backgroundColor: "#9F4223",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  )
}

export default Notification
